import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Search as SearchIcon, Loader2 } from 'lucide-react';
import MapCard from './MapCard';
import MapModal from './MapModal';
import { fetchMapsLightweight, fetchMapById } from '../services/api';

// Helper function to extract numeric year from year string
const extractYearRange = (yearStr) => {
  if (!yearStr) return { min: null, max: null };
  
  const lowerYear = yearStr.toLowerCase();
  
  // Check if this is a map reconstructing ancient period
  const ancientMatch = lowerYear.match(/(\d+)\s*d\.?\s*hr/i);
  if (ancientMatch) {
    const ancientYear = parseInt(ancientMatch[1], 10);
    if (ancientYear < 500) {
      const printMatch = yearStr.match(/tip[aă]rit\s*(\d{4})/i) || yearStr.match(/\((\d{4})\)/);
      return {
        min: ancientYear,
        max: printMatch ? parseInt(printMatch[1], 10) : ancientYear
      };
    }
  }
  
  const matches = yearStr.match(/\b(1[0-9]{3}|20[0-9]{2})\b/g);
  
  if (!matches || matches.length === 0) {
    const threeDigitMatches = yearStr.match(/\b([1-9][0-9]{2})\b/g);
    if (threeDigitMatches) {
      const years = threeDigitMatches.map(y => parseInt(y, 10)).filter(y => y >= 100 && y < 1000);
      if (years.length > 0) {
        return {
          min: Math.min(...years),
          max: Math.max(...years)
        };
      }
    }
    return { min: null, max: null };
  }
  
  const years = matches.map(y => parseInt(y, 10));
  return {
    min: Math.min(...years),
    max: Math.max(...years)
  };
};

// Category definitions with order and display names
const categoryDefinitions = [
  {
    id: 'dobrogea',
    displayName: 'Dobrogea',
    regions: ['dobrogea'],
    // ONLY "Oestliche Hælffe" (Eastern half) is for Dobrogea - NOT "Westliche" (Western)
    includeTitles: ['BULGARIEN Oestliche'],
    // Explicitly exclude Western half from Dobrogea
    excludeTitles: ['BULGARIEN Westliche']
  },
  {
    id: 'dacia',
    displayName: 'Dacia',
    regions: ['dacia'],
    // Include Regiones Danubianae in Dacia (NOT 2 Transsylvania XXI)
    includeTitles: ['Regiones Danubianae'],
    excludeTitles: ['2 Transsylvania']
  },
  {
    id: 'valahia-moldova-transilvania',
    displayName: 'Valahia/Moldova/Transilvania',
    regions: ['moldova', 'tara-romaneasca', 'valahia', 'transilvania', 'banat', 'valahia-moldova-transilvania'],
    // Include maps related to Moldova, Valahia, Tara Romaneasca, Transilvania, Banat
    // NOTE: "DIE LANDSCHAFT ROMANIEN" a fost scoasă de aici — regiunea ei reală este
    // 'rumelia', care aparține deja de Imperiul Otoman, deci se clasează corect singură.
    includeTitles: [
      'Moldova', 'Valahia', 'Valachia', 'Walachia', 'Wallachia', 'Moldavia',
      'Principati di Moldavia', 'Carte der Wallachei',
      'TRANSYLVANIA',
      'Banatul de Sud-Vest', 'Nova Tabula Transylvaniae', 'Transylvania - Hartă Mercator',
      '2 Transsylvania XXI Nova Tabula', 'La Moldavie, La Valaquie'
    ],
    // Exclude Ortelius "Romaniae quae olim Thracia" — title contains "Walachiae" as a substring
    // but this is a general Balkans/Europe map, not a Walachia-specific map
    excludeTitles: ['olim Thracia']
  },
  {
    id: 'imperiul-otoman',
    displayName: 'Imperiul Otoman',
    regions: ['imperiu-otoman', 'bosfor', 'rumelia'],
    // Include Turkey and Ottoman maps
    includeTitles: [
      'TURKEY IN EUROPE', 'TURQUIE D\'EUROPE', 'TURKEY in EUROPE', 
      'LA TURQUIE en Europe', 'TURKEY IN EUROPE AND GREECE',
      'TURKEY IN EUROPE AND HUNGARY',
      'IMPERIUM TURCICUM in EUROPA ASIA et AFRICA',
      'CARTE DE LA TURQUIE EUROPEENNE',
      'CARTE DE L\'EMPIRE OTTOMAN EN EUROPE',
      // Mutate din Europa la cerere
      'Turcicum Imperium',        // Frederick de Wit, 1680
      'BULGARIEN Westliche'       // Des Koenigreichs Bulgarien Westliche Hælfte
    ]
  },
  {
    id: 'europa',
    displayName: 'Europa',
    regions: [
      'europa', 'europa-antica', 'europa-sud-est',
      'balkani', 'bulgaria', 'ungaria', 'grecia',
      'marea-neagra', 'mediterana'
    ],
    // Include specific Europe maps
    includeTitles: [
      'EUROPE - La Métropole', 'CARTE GÉNÉRALE DE L\'EUROPE'
    ]
  },
  {
    id: 'lumea-antica',
    displayName: 'Lumea Antică',
    regions: ['lumea-antica', 'imperiu-roman', 'scythia', 'imperiul-macedonean'],
    // Mutata din Europa la cerere: Coronelli, Tabula Topographica Athenarum
    // Veterum et Novarum (1720). Regiunea din baza de date ramane 'europa'.
    includeTitles: ['Athenarum']
  },
  {
    id: 'asia',
    displayName: 'Asia',
    regions: ['asia', 'asia-mica', 'terra-sancta']
  }
];

// Region grouping mapping for filter
const regionMapping = {
  'dobrogea': ['dobrogea'],
  'dacia': ['dacia'],
  'valahia-moldova': ['moldova', 'tara-romaneasca', 'valahia'],
  'imperiul-otoman': ['imperiu-otoman', 'bosfor', 'rumelia'],
  'europa': [
    'europa', 'europa-antica', 'europa-sud-est',
    'transilvania', 'banat',
    'balkani', 'bulgaria', 'ungaria', 'grecia',
    'marea-neagra', 'mediterana'
  ],
  'lumea-antica': ['lumea-antica', 'imperiu-roman', 'scythia', 'imperiul-macedonean'],
  'asia': ['asia', 'asia-mica', 'terra-sancta'],
};

// Search keywords mapping
const searchKeywords = {
  'turcia': ['turquie', 'turkey', 'turcicum', 'otoman', 'ottoman', 'turcia', 'turc', 'turcici', 'turcarum'],
  'romania': ['romania', 'român', 'roumanie', 'dacia', 'romanian', 'romanien', 'românie', 'românesc'],
  'valahia': ['walachia', 'valahia', 'valachia', 'wallachia', 'țara românească', 'tara-romaneasca', 'valachiae'],
  'walachia': ['walachia', 'valahia', 'valachia', 'wallachia', 'țara românească', 'tara-romaneasca', 'valachiae'],
  'bulgaria': ['bulgaria', 'bvlgaria', 'bulgarien', 'bulgarie', 'bulgariei'],
  'serbia': ['serbia', 'servia', 'serbien', 'serbie', 'serbiei'],
  'europa': ['europe', 'europa', 'evropae', 'européenne', 'europäische', 'europae'],
  'balcani': ['balkan', 'balkani', 'balkans', 'balcani', 'halbinsel', 'peninsula'],
  'grecia': ['greece', 'grecia', 'grèce', 'grecesc', 'greci', 'graecia', 'graeciae', 'elada', 'helen'],
  'ungaria': ['hungary', 'ungaria', 'hongrie', 'ungarn', 'hungariae', 'magyar'],
  'moldova': ['moldova', 'moldavia', 'moldavie', 'moldau', 'moldaviae', 'bogdan'],
  'transilvania': ['transylvania', 'transilvania', 'transylvanie', 'ardeal', 'siebenbürgen', 'transylvaniae', 'septem', 'castra'],
  'dobrogea': ['dobrogea', 'dobruja', 'dobrudja', 'sulina', 'tulcea', 'babadag', 'cadrilater'],
  'dunare': ['danube', 'danubii', 'danubiu', 'donau', 'istru', 'ister'],
  'marea neagra': ['black sea', 'pont euxin', 'pontus euxinus', 'marea-neagra', 'euxin', 'maris nigri', 'pontic'],
  'mediterana': ['mediterranean', 'méditerranée', 'mediterana', 'mare nostrum'],
  'bosfor': ['bosphore', 'bosphor', 'bosfor', 'constantinopol', 'istanbul', 'bizant', 'byzantium'],
  'asia': ['asia', 'asie', 'asiae', 'anatolia', 'asia-mica', 'asia minor'],
  'carpati': ['carpathian', 'carpați', 'carpates', 'munți', 'carpathici'],
  'otoman': ['ottoman', 'otoman', 'turcicum', 'turquie', 'turkey', 'turc', 'sublime', 'poartă'],
  'imperiu': ['empire', 'imperium', 'imperiu', 'reich', 'imperii'],
  'roman': ['roman', 'romain', 'rome', 'roma', 'romanum', 'latinesc', 'romae'],
  'antic': ['ancient', 'antic', 'anciens', 'antichitate', 'antiquity', 'vechi', 'străvechi'],
  'bizantin': ['byzantin', 'bizantin', 'constantinopol', 'thrace', 'tracia', 'byzantium'],
  'medieval': ['medieval', 'evul mediu', 'middle ages', 'feudal'],
  'harta': ['carte', 'map', 'tabula', 'tabvla', 'mappa', 'chart', 'charta'],
  'general': ['general', 'générale', 'generale', 'overview', 'ansamblu'],
  'descriptio': ['descriptio', 'descriere', 'description', 'descrierea'],
  'nova': ['nova', 'nouă', 'new', 'nouvelle', 'novum'],
  'dacia': ['dacia', 'dacian', 'geto-dac', 'daci', 'daciae', 'dacilor', 'dacic'],
  'tracia': ['thrace', 'tracia', 'tracian', 'romanien', 'thraciae'],
  'pannonia': ['pannonia', 'pannonian', 'panonia', 'pannoniae'],
  'illiria': ['illyricum', 'illyria', 'illiria', 'iliric', 'illyriae'],
  'moesia': ['moesia', 'moesian', 'mesia', 'moesiae'],
  'rumelia': ['rumelia', 'roumelia', 'rumelien', 'romanien'],
  'scythia': ['scythia', 'sciția', 'scythian', 'scitia', 'scythiae'],
  'munster': ['münster', 'munster', 'sebastian', 'cosmographia'],
  'ptolemeu': ['ptolemy', 'ptolemeu', 'ptolemaeus', 'claudius', 'ptolemaică'],
  'blaeu': ['blaeu', 'willem', 'joan', 'theatrum'],
  'mercator': ['mercator', 'gerhard', 'gerardus'],
  'ortelius': ['ortelius', 'abraham', 'theatrum orbis'],
  'homann': ['homann', 'johann', 'heirs'],
  'sanson': ['sanson', 'nicolas', 'd\'anville'],
  'sduk': ['sduk', 'useful knowledge', 'society'],
  'principat': ['principatus', 'principat', 'principatele', 'prinț', 'voievodatul'],
  'regate': ['regnum', 'regat', 'kingdom', 'konigreich', 'royaume'],
  'provintie': ['provincia', 'provintie', 'province', 'provinz'],
};

// Function to check if a search query matches a map
const matchesSearchKeywords = (query, map) => {
  const lowerQuery = query.toLowerCase().trim();
  
  if (map.title.toLowerCase().includes(lowerQuery) ||
      map.author.toLowerCase().includes(lowerQuery) ||
      map.region.toLowerCase().includes(lowerQuery)) {
    return true;
  }
  
  if (map.tags && Array.isArray(map.tags)) {
    if (map.tags.some(tag => tag.toLowerCase().includes(lowerQuery))) {
      return true;
    }
  }
  
  if (map.description && map.description.toLowerCase().includes(lowerQuery)) {
    return true;
  }
  
  for (const [keyword, synonyms] of Object.entries(searchKeywords)) {
    if (keyword.includes(lowerQuery) || synonyms.some(syn => syn.toLowerCase().includes(lowerQuery))) {
      const mapText = `${map.title} ${map.author} ${map.region} ${map.tags?.join(' ') || ''} ${map.description || ''}`.toLowerCase();
      if (synonyms.some(syn => mapText.includes(syn.toLowerCase())) || mapText.includes(keyword)) {
        return true;
      }
    }
  }
  
  return false;
};

// Function to categorize a map
const categorizeMap = (map) => {
  // First check explicit exclusions
  for (const category of categoryDefinitions) {
    if (category.excludeTitles) {
      for (const titleMatch of category.excludeTitles) {
        if (map.title.toUpperCase().includes(titleMatch.toUpperCase())) {
          // This map is excluded from this category, continue to next category
          continue;
        }
      }
    }
  }
  
  // Check special title matches for specific categorization
  for (const category of categoryDefinitions) {
    if (category.includeTitles) {
      for (const titleMatch of category.includeTitles) {
        if (map.title.toUpperCase().includes(titleMatch.toUpperCase())) {
          // Check if this map is excluded from this category
          if (category.excludeTitles) {
            const isExcluded = category.excludeTitles.some(excl => 
              map.title.toUpperCase().includes(excl.toUpperCase())
            );
            if (isExcluded) continue;
          }
          return category.id;
        }
      }
    }
  }
  
  // Then check by region
  for (const category of categoryDefinitions) {
    if (category.regions.includes(map.region)) {
      return category.id;
    }
  }
  
  // Default to Europa if no match
  return 'europa';
};

// Category Header Component
const CategoryHeader = ({ name }) => (
  <div className="col-span-full mt-8 mb-4 first:mt-0">
    <h3 
      className="text-lg font-serif font-semibold text-[#5c4a3d] tracking-wide uppercase"
      style={{ fontFamily: 'Georgia, serif' }}
    >
      {name}
    </h3>
    <div className="h-[2px] w-16 bg-[#d4af37] mt-2"></div>
  </div>
);

const MapGallery = ({ searchQuery, yearRange, regionFilter, viewMode }) => {
  const [selectedMap, setSelectedMap] = useState(null);
  const [allMaps, setAllMaps] = useState([]); // All maps from API (lightweight)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleCount, setVisibleCount] = useState(20); // Progressive rendering
  const loadMoreRef = useRef(null);
  const RENDER_BATCH = 20;

  // Load ALL maps once (lightweight - ~56KB)
  useEffect(() => {
    const loadAllMaps = async () => {
      try {
        setLoading(true);
        const data = await fetchMapsLightweight();
        setAllMaps(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching maps:', err);
        setError('Failed to load maps. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadAllMaps();
  }, []);

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(20);
  }, [searchQuery, regionFilter, yearRange]);

  // Filter maps based on search, region, and year
  const filteredMaps = useMemo(() => {
    return allMaps.filter(map => {
      // Search filter
      const matchesSearch = searchQuery === '' || matchesSearchKeywords(searchQuery, map);

      // Region filter
      let matchesRegion = regionFilter === 'all';
      if (!matchesRegion && regionMapping[regionFilter]) {
        matchesRegion = regionMapping[regionFilter].includes(map.region);
      } else if (!matchesRegion) {
        matchesRegion = map.region === regionFilter;
      }

      // Year filter
      let matchesYear = true;
      if (yearRange && yearRange[0] && yearRange[1]) {
        const mapYearData = extractYearRange(map.year);
        if (mapYearData.min !== null && mapYearData.max !== null) {
          matchesYear = mapYearData.max >= yearRange[0] && mapYearData.min <= yearRange[1];
        }
      }

      return matchesSearch && matchesRegion && matchesYear;
    });
  }, [allMaps, searchQuery, yearRange, regionFilter]);

  // Progressive rendering - show only visibleCount maps
  const mapsToRender = useMemo(() => {
    return filteredMaps.slice(0, visibleCount);
  }, [filteredMaps, visibleCount]);

  const hasMore = visibleCount < filteredMaps.length;

  // Infinite scroll for progressive rendering
  useEffect(() => {
    if (!loadMoreRef.current || loading || !hasMore) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setVisibleCount(prev => Math.min(prev + RENDER_BATCH, filteredMaps.length));
        }
      },
      { threshold: 0.1, rootMargin: '200px' }
    );
    
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, filteredMaps.length]);

  // Group ALL filtered maps by category (for complete category structure)
  const groupedMaps = useMemo(() => {
    const groups = {};
    
    // Initialize all categories in order
    categoryDefinitions.forEach(cat => {
      groups[cat.id] = [];
    });
    
    // Categorize each filtered map
    filteredMaps.forEach(map => {
      const categoryId = categorizeMap(map);
      if (groups[categoryId]) {
        groups[categoryId].push(map);
      } else {
        // Fallback to europa
        groups['europa'].push(map);
      }
    });
    
    return groups;
  }, [filteredMaps]);

  // Create a flat ordered list respecting category order for progressive rendering
  const orderedMaps = useMemo(() => {
    const result = [];
    categoryDefinitions.forEach(category => {
      const categoryMaps = groupedMaps[category.id] || [];
      categoryMaps.forEach(map => {
        result.push({ ...map, _category: category.id });
      });
    });
    return result;
  }, [groupedMaps]);

  // Maps to render progressively (respects category order)
  const mapsToRenderSet = useMemo(() => {
    const renderIds = new Set();
    orderedMaps.slice(0, visibleCount).forEach(map => renderIds.add(map.id));
    return renderIds;
  }, [orderedMaps, visibleCount]);

  // Check if we should show categories (only when no specific filter is applied)
  const showCategories = searchQuery === '' && regionFilter === 'all';

  // Handle map click - fetch full details before showing modal
  const handleMapClick = useCallback(async (map) => {
    try {
      const fullMapData = await fetchMapById(map.id);
      setSelectedMap(fullMapData);
    } catch (err) {
      console.error('Error fetching map details:', err);
      // Fallback to partial data if fetch fails
      setSelectedMap(map);
    }
  }, []);

  const handleCloseModal = () => {
    setSelectedMap(null);
  };

  const handleNextMap = useCallback(async () => {
    const currentIndex = filteredMaps.findIndex(m => m.id === selectedMap.id);
    const nextIndex = (currentIndex + 1) % filteredMaps.length;
    const nextMap = filteredMaps[nextIndex];
    try {
      const fullMapData = await fetchMapById(nextMap.id);
      setSelectedMap(fullMapData);
    } catch (err) {
      setSelectedMap(nextMap);
    }
  }, [filteredMaps, selectedMap]);

  const handlePrevMap = useCallback(async () => {
    const currentIndex = filteredMaps.findIndex(m => m.id === selectedMap.id);
    const prevIndex = (currentIndex - 1 + filteredMaps.length) % filteredMaps.length;
    const prevMap = filteredMaps[prevIndex];
    try {
      const fullMapData = await fetchMapById(prevMap.id);
      setSelectedMap(fullMapData);
    } catch (err) {
      setSelectedMap(prevMap);
    }
  }, [filteredMaps, selectedMap]);

  // Render maps grouped by category (progressive - only renders maps in mapsToRenderSet)
  const renderGroupedMaps = () => {
    return categoryDefinitions.map(category => {
      const categoryMaps = groupedMaps[category.id];
      
      // Skip empty categories
      if (!categoryMaps || categoryMaps.length === 0) {
        return null;
      }
      
      // Filter to only show maps that should be rendered
      const visibleCategoryMaps = categoryMaps.filter(map => mapsToRenderSet.has(map.id));
      
      // Skip if no visible maps in this category yet
      if (visibleCategoryMaps.length === 0) {
        return null;
      }
      
      return (
        <React.Fragment key={category.id}>
          <CategoryHeader name={category.displayName} />
          {visibleCategoryMaps.map(map => (
            <MapCard
              key={map.id}
              map={map}
              onClick={handleMapClick}
              viewMode={viewMode}
            />
          ))}
        </React.Fragment>
      );
    });
  };

  // Render flat list (when filters are applied) - progressive
  const renderFlatMaps = () => {
    return orderedMaps.slice(0, visibleCount).map(map => (
      <MapCard
        key={map.id}
        map={map}
        onClick={handleMapClick}
        viewMode={viewMode}
      />
    ));
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <p className="text-xl md:text-2xl font-serif text-gray-700 mb-3">
            Asociația Culturală Dobrogenius
          </p>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-4">
            Colecția Completă de Hărți Interactive
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Fiecare hartă poate fi explorată în detaliu. Click pentru vedere mărită cu funcționalitate de zoom avansat.
          </p>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-16 bg-white rounded-xl shadow-sm">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#d4af37] mx-auto mb-4"></div>
            <h3 className="text-2xl font-bold text-gray-700 mb-2">Se încarcă hărțile...</h3>
            <p className="text-gray-600">Vă rugăm să așteptați.</p>
          </div>
        ) : error ? (
          <div className="text-center py-16 bg-white rounded-xl shadow-sm">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h3 className="text-2xl font-bold text-gray-700 mb-2">Eroare la încărcare</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-[#d4af37] text-white px-6 py-2 rounded-lg hover:bg-[#b8941f] transition-colors"
            >
              Încearcă din nou
            </button>
          </div>
        ) : (
          <>
            {/* Maps Grid/List */}
            {filteredMaps.length > 0 ? (
              <>
                <div className={viewMode === 'grid' 
                  ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8'
                  : 'space-y-6'
                }>
                  {showCategories ? renderGroupedMaps() : renderFlatMaps()}
                </div>
                
                {/* Infinite scroll trigger for progressive rendering */}
                {hasMore && (
                  <div 
                    ref={loadMoreRef} 
                    className="flex justify-center items-center py-8 mt-8"
                  >
                    <div className="flex items-center gap-3 text-gray-600">
                      <Loader2 className="w-6 h-6 animate-spin text-[#d4af37]" />
                      <span>Se încarcă mai multe hărți...</span>
                    </div>
                  </div>
                )}
                
                {!hasMore && filteredMaps.length > 0 && (
                  <div className="text-center py-8 mt-8 text-gray-500">
                    Toate cele {filteredMaps.length} hărți sunt afișate
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-16 bg-white rounded-xl shadow-sm">
                <SearchIcon size={64} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-2xl font-bold text-gray-700 mb-2">Nu s-au găsit rezultate</h3>
                <p className="text-gray-600">Încearcă să modifiți criteriile de căutare sau filtrele.</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {selectedMap && (
        <MapModal
          map={selectedMap}
          onClose={handleCloseModal}
          onNext={handleNextMap}
          onPrev={handlePrevMap}
        />
      )}
    </section>
  );
};

export default MapGallery;
